import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getPublicPlayersApi,
} from '../api/player.api';

import Card from '../components/common/Card';
import Icon from '../components/common/Icon';
import Input from '../components/common/Input';
import Pagination from '../components/common/Pagination';

import {
  AGE_GROUP_LABELS,
  GENDER_LABELS,
} from '../utils/constants';

const POSITION_LABELS = {
  GOALKEEPER: 'دروازه‌ بان',
  DEFENDER: 'مدافع',
  MIDFIELDER: 'هافبک',
  FORWARD: 'مهاجم',
};

function getImageUrl(path) {
  if (!path) return null;

  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('blob:')
  ) {
    return path;
  }

  const base =
    process.env.REACT_APP_API_BASE_URL
      ?.replace('/api/v1', '') || '';

  return `${base}${path}`;
}

function getFullName(player) {
  return (
    `${player?.firstName || ''} ${
      player?.lastName || ''
    }`.trim() || 'بازیکن'
  );
}

function getPosition(player) {
  return (
    POSITION_LABELS[player?.mainPosition] ||
    POSITION_LABELS[player?.position] ||
    player?.position ||
    'پست ثبت نشده'
  );
}

function getInitials(player) {
  const first =
    player?.firstName?.charAt(0) || '';

  const last =
    player?.lastName?.charAt(0) || '';

  return `${first}${last}` || '⚽';
}

function PlayerCard({ player }) {
  const name = getFullName(player);
  const photo = getImageUrl(player.photo);

  return (
    <Link
      to={`/players/${player.id}`}
      className="group block"
    >
      <Card
        hover
        className="
          relative overflow-hidden
          rounded-[1.75rem]
          border border-slate-200
          bg-white
          p-0
          transition-all duration-500
          hover:-translate-y-2
          hover:border-emerald-200
          hover:shadow-2xl
          hover:shadow-emerald-900/10
        "
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-l from-emerald-400 via-emerald-500 to-lime-400" />

        <div className="relative p-5">
          <div className="flex items-center gap-4">
            <div className="relative h-[78px] w-[78px] shrink-0">
              <div className="
                absolute inset-0 rounded-[1.5rem]
                bg-gradient-to-br
                from-emerald-400
                to-emerald-700
                opacity-10
                blur-xl
              " />

              <div className="
                relative grid h-full w-full
                place-items-center
                overflow-hidden
                rounded-[1.5rem]
                border border-emerald-100
                bg-gradient-to-br
                from-emerald-50
                to-slate-50
                shadow-sm
              ">
                {photo ? (
                  <img
                    src={photo}
                    alt={name}
                    loading="lazy"
                    className="
                      h-full w-full
                      object-cover
                      transition-transform
                      duration-500
                      group-hover:scale-110
                    "
                    onError={(event) => {
                      event.currentTarget.style.display =
                        'none';
                    }}
                  />
                ) : (
                  <span className="
                    text-xl
                    font-black
                    text-emerald-600
                  ">
                    {getInitials(player)}
                  </span>
                )}
              </div>

              {player.jerseyNumber !== null &&
                player.jerseyNumber !== undefined && (
                  <span className="
                    absolute -bottom-2 -right-2
                    grid h-7 w-7
                    place-items-center
                    rounded-full
                    border-2 border-white
                    bg-[#0B1F17]
                    text-[10px]
                    font-black
                    text-white
                    shadow-lg
                  ">
                    {player.jerseyNumber}
                  </span>
                )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="
                    truncate
                    text-base
                    font-black
                    text-slate-900
                    transition-colors
                    group-hover:text-emerald-600
                  ">
                    {name}
                  </h2>

                  <p className="
                    mt-1
                    text-xs
                    font-bold
                    text-emerald-600
                  ">
                    {getPosition(player)}
                  </p>
                </div>

                <span className="
                  grid h-8 w-8
                  shrink-0
                  place-items-center
                  rounded-full
                  bg-slate-50
                  text-slate-300
                  transition-all
                  group-hover:bg-emerald-50
                  group-hover:text-emerald-600
                ">
                  <Icon
                    name="arrowLeft"
                    className="h-4 w-4"
                  />
                </span>
              </div>

              {player.club && (
                <div className="
                  mt-3 flex min-w-0
                  items-center gap-2
                ">
                  <div className="
                    grid h-6 w-6
                    shrink-0
                    place-items-center
                    overflow-hidden
                    rounded-lg
                    border border-slate-100
                    bg-slate-50
                  ">
                    {player.club.logo ? (
                      <img
                        src={getImageUrl(
                          player.club.logo
                        )}
                        alt=""
                        className="
                          h-full w-full
                          object-contain
                          p-1
                        "
                      />
                    ) : (
                      <Icon
                        name="shield"
                        className="
                          h-3.5 w-3.5
                          text-slate-300
                        "
                      />
                    )}
                  </div>

                  <span className="
                    truncate
                    text-[11px]
                    font-bold
                    text-slate-500
                  ">
                    {player.club.name}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="
            mt-5
            grid grid-cols-3
            gap-2
            border-t
            border-slate-100
            pt-4
          ">
            <MiniStat
              label="رده"
              value={
                AGE_GROUP_LABELS[
                  player.ageGroup
                ] ||
                player.ageGroup ||
                '—'
              }
            />

            <MiniStat
              label="گل"
              value={player.goals ?? 0}
            />

            <MiniStat
              label="پاس گل"
              value={player.assists ?? 0}
            />
          </div>
        </div>

        <div className="
          absolute inset-x-0 bottom-0
          h-0.5
          origin-right
          scale-x-0
          bg-gradient-to-l
          from-emerald-500
          to-lime-400
          transition-transform
          duration-500
          group-hover:scale-x-100
        " />
      </Card>
    </Link>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="
      rounded-xl
      bg-slate-50
      px-2
      py-2
      text-center
    ">
      <div className="
        truncate
        text-[10px]
        font-black
        text-slate-700
      ">
        {value}
      </div>

      <div className="
        mt-0.5
        text-[9px]
        text-slate-400
      ">
        {label}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="
      animate-pulse
      rounded-[1.75rem]
      border
      border-slate-200
      bg-white
      p-5
    ">
      <div className="flex items-center gap-4">
        <div className="
          h-[78px] w-[78px]
          rounded-[1.5rem]
          bg-slate-100
        " />

        <div className="flex-1 space-y-3">
          <div className="
            h-4 w-3/4
            rounded bg-slate-100
          " />

          <div className="
            h-3 w-1/2
            rounded bg-slate-100
          " />

          <div className="
            h-3 w-2/3
            rounded bg-slate-100
          " />
        </div>
      </div>

      <div className="
        mt-5 h-10
        rounded-xl
        bg-slate-100
      " />
    </div>
  );
}

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] =
    useState('');

  const [filters, setFilters] =
    useState({
      ageGroup: '',
      gender: '',
    });

  const [pagination, setPagination] =
    useState({
      page: 1,
      limit: 12,
      total: 0,
      totalPages: 1,
    });

  useEffect(() => {
    let mounted = true;

    const timer = setTimeout(async () => {
      setLoading(true);

      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
        };

        if (search.trim()) {
          params.search = search.trim();
        }

        if (filters.ageGroup) {
          params.ageGroup =
            filters.ageGroup;
        }

        if (filters.gender) {
          params.gender =
            filters.gender;
        }

        const { data } =
          await getPublicPlayersApi(params);

        if (!mounted) return;

        setPlayers(
          Array.isArray(data?.data)
            ? data.data
            : []
        );

        setPagination((prev) => ({
          ...prev,
          total:
            Number(data?.meta?.total) || 0,
          totalPages:
            Number(data?.meta?.totalPages) || 1,
        }));
      } catch {
        if (mounted) {
          setPlayers([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [
    search,
    filters,
    pagination.page,
    pagination.limit,
  ]);

  const hasFilters =
    Boolean(search) ||
    Boolean(filters.ageGroup) ||
    Boolean(filters.gender);

  function changeFilter(key, value) {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  }

  function clearFilters() {
    setSearch('');
    setFilters({
      ageGroup: '',
      gender: '',
    });

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  }

  function handleSearch(event) {
    setSearch(event.target.value);

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  }

  return (
    <main className="overflow-hidden">
      {/* Header */}
      <section className="hero-panel relative isolate mx-3 overflow-hidden rounded-[2.25rem] bg-[#07150F] py-16 shadow-[0_50px_100px_-40px_rgba(7,21,15,0.7)] sm:mx-5 sm:rounded-[3rem] sm:py-20">
        <div className="
          pointer-events-none
          absolute -right-32 -top-32
          h-80 w-80
          rounded-full
          bg-emerald-500/10
          blur-3xl
        " />

        <div className="
          pointer-events-none
          absolute -bottom-40 -left-20
          h-96 w-96
          rounded-full
          bg-lime-500/10
          blur-3xl
        " />

        <div className="
          container-page
          relative z-10
        ">
          <div className="max-w-3xl">
            <span className="
              inline-flex
              items-center gap-2
              rounded-full
              border border-emerald-400/20
              bg-emerald-500/10
              px-4 py-2
              text-[11px]
              font-black
              text-emerald-300
            ">
              <span className="
                h-1.5 w-1.5
                animate-pulse
                rounded-full
                bg-emerald-400
              " />
              جامعه بازیکنان فوتبال
            </span>

            <h1 className="
              mt-5
              text-4xl
              font-black
              leading-tight
              text-white
              sm:text-5xl
            ">
              ستاره‌های
              <span className="
                bg-gradient-to-l
                from-emerald-300
                to-lime-300
                bg-clip-text
                text-transparent
              ">
                {' '}فوتبال شهر
              </span>
            </h1>

            <p className="
              mt-5
              max-w-2xl
              text-sm
              leading-8
              text-white/60
              sm:text-base
            ">
              بازیکنان فعال باشگاه‌های عضو
              سامانه را جستجو کنید و پروفایل
              حرفه‌ای هر بازیکن را مشاهده کنید.
            </p>
          </div>

          <div className="
            mt-8
            grid max-w-xl
            grid-cols-2
            gap-3
            sm:grid-cols-3
          ">
            <HeroStat
              icon="users"
              value={
                loading
                  ? '—'
                  : pagination.total
              }
              label="بازیکن فعال"
            />

            <HeroStat
              icon="shield"
              value="باشگاه"
              label="عضویت رسمی"
            />

            <HeroStat
              icon="trophy"
              value="LIVE"
              label="اطلاعات آنلاین"
            />
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="
        container-page
        py-12 sm:py-16
      ">
        <div className="
          mb-8
          rounded-[1.75rem]
          border
          border-slate-200
          bg-white
          p-4
          shadow-sm
        ">
          <div className="
            flex flex-col
            gap-3
            lg:flex-row
            lg:items-center
          ">
            <div className="
              relative
              flex-1
            ">
              <Input
                value={search}
                onChange={handleSearch}
                placeholder="
                  جستجوی نام بازیکن...
                "
                className="
                  h-12
                  pr-11
                  rounded-2xl
                  border-slate-200
                "
              />

              <Icon
                name="search"
                className="
                  pointer-events-none
                  absolute right-4 top-1/2
                  h-5 w-5
                  -translate-y-1/2
                  text-slate-400
                "
              />
            </div>

            <select
              value={filters.ageGroup}
              onChange={(event) =>
                changeFilter(
                  'ageGroup',
                  event.target.value
                )
              }
              className="
                h-12
                rounded-2xl
                border border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-600
                outline-none
                focus:border-emerald-400
                focus:ring-4
                focus:ring-emerald-500/10
              "
            >
              <option value="">
                همه رده‌های سنی
              </option>

              {Object.entries(
                AGE_GROUP_LABELS
              ).map(([key, label]) => (
                <option
                  key={key}
                  value={key}
                >
                  {label}
                </option>
              ))}
            </select>

            <select
              value={filters.gender}
              onChange={(event) =>
                changeFilter(
                  'gender',
                  event.target.value
                )
              }
              className="
                h-12
                rounded-2xl
                border border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-600
                outline-none
                focus:border-emerald-400
                focus:ring-4
                focus:ring-emerald-500/10
              "
            >
              <option value="">
                همه جنسیت‌ها
              </option>

              {Object.entries(
                GENDER_LABELS
              ).map(([key, label]) => (
                <option
                  key={key}
                  value={key}
                >
                  {label}
                </option>
              ))}
            </select>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="
                  h-12
                  rounded-2xl
                  border border-red-100
                  bg-red-50
                  px-5
                  text-sm
                  font-black
                  text-red-500
                  transition
                  hover:bg-red-100
                "
              >
                پاک کردن
              </button>
            )}
          </div>
        </div>

        <div className="
          mb-7
          flex items-center
          justify-between
        ">
          <div>
            <h2 className="
              text-xl
              font-black
              text-[#0B1F17]
            ">
              بازیکنان
            </h2>

            <p className="
              mt-1
              text-xs
              text-slate-400
            ">
              {loading
                ? 'در حال دریافت اطلاعات...'
                : `${pagination.total.toLocaleString(
                    'fa-IR'
                  )} بازیکن`}
            </p>
          </div>

          <div className="
            hidden
            rounded-full
            bg-emerald-50
            px-4 py-2
            text-xs
            font-black
            text-emerald-600
            sm:block
          ">
            اطلاعات رسمی
          </div>
        </div>

        {loading ? (
          <div className="
            grid gap-5
            sm:grid-cols-2
            lg:grid-cols-3
            xl:grid-cols-4
          ">
            {Array.from({
              length: 8,
            }).map((_, index) => (
              <SkeletonCard
                key={index}
              />
            ))}
          </div>
        ) : players.length === 0 ? (
          <div className="
            rounded-[2rem]
            border border-dashed
            border-slate-200
            bg-white
            px-6 py-20
            text-center
          ">
            <div className="
              mx-auto grid
              h-16 w-16
              place-items-center
              rounded-3xl
              bg-slate-50
              text-slate-300
            ">
              <Icon
                name="users"
                className="h-8 w-8"
              />
            </div>

            <h3 className="
              mt-5
              text-lg
              font-black
              text-slate-700
            ">
              بازیکنی پیدا نشد
            </h3>

            <p className="
              mx-auto mt-2
              max-w-md
              text-sm
              leading-7
              text-slate-400
            ">
              عبارت جستجو یا فیلترهای
              انتخاب‌ شده را تغییر دهید.
            </p>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="
                  mt-6
                  rounded-2xl
                  bg-emerald-600
                  px-6 py-3
                  text-sm
                  font-black
                  text-white
                  shadow-lg
                  shadow-emerald-500/20
                  transition
                  hover:-translate-y-0.5
                  hover:bg-emerald-700
                "
              >
                نمایش همه بازیکنان
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="
              grid gap-5
              sm:grid-cols-2
              lg:grid-cols-3
              xl:grid-cols-4
            ">
              {players.map((player) => (
                <PlayerCard
                  key={player.id}
                  player={player}
                />
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div className="mt-10">
                <Pagination
                  page={pagination.page}
                  totalPages={
                    pagination.totalPages
                  }
                  onPageChange={(page) =>
                    setPagination((prev) => ({
                      ...prev,
                      page,
                    }))
                  }
                />
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}

function HeroStat({
  icon,
  value,
  label,
}) {
  return (
    <div className="
      rounded-2xl
      border border-white/10
      bg-white/[0.06]
      p-4
      backdrop-blur-xl
    ">
      <div className="
        flex items-center
        gap-3
      ">
        <span className="
          grid h-10 w-10
          place-items-center
          rounded-xl
          bg-emerald-400/10
          text-emerald-300
        ">
          <Icon
            name={icon}
            className="h-5 w-5"
          />
        </span>

        <div>
          <div className="
            text-lg
            font-black
            text-white
          ">
            {value}
          </div>

          <div className="
            text-[10px]
            text-white/45
          ">
            {label}
          </div>
        </div>
      </div>
    </div>
  );
}